import { useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface FieldErrors {
  name?: string;
  country?: string;
  general?: string;
}

const ERROR_CODE_MESSAGES: Record<string, string> = {
  already_in_organization: 'You already belong to an organization. Try refreshing the page.',
  unauthenticated: 'Your session has expired. Please sign in again.',
  internal_error: 'Something went wrong on our end. Please try again.',
};

function friendlyZodMessage(field: string, raw: string): string {
  if (field === 'name') {
    if (/at least 2/.test(raw)) return 'Give your organization a name with at least 2 characters.';
    if (/at most 120/.test(raw)) return 'Organization name is too long (max 120 characters).';
  }
  if (field === 'country') {
    if (/exactly 2/.test(raw)) return 'Use a 2-letter ISO country code (e.g. DK, US) or leave it blank.';
  }
  return raw;
}

function parseError(err: unknown): FieldErrors {
  if (!(err instanceof ApiError)) {
    return { general: err instanceof Error ? err.message : 'Something went wrong.' };
  }
  const body = err.body as
    | { error?: string; issues?: { fieldErrors?: Record<string, string[]> } }
    | null;
  const fieldErrors = body?.issues?.fieldErrors;
  if (fieldErrors && Object.keys(fieldErrors).length > 0) {
    const out: FieldErrors = {};
    for (const [field, msgs] of Object.entries(fieldErrors)) {
      const first = msgs?.[0];
      if (!first) continue;
      if (field === 'name' || field === 'country') {
        out[field] = friendlyZodMessage(field, first);
      } else {
        out.general = `${field}: ${first}`;
      }
    }
    return out;
  }
  const code = body?.error;
  if (code && ERROR_CODE_MESSAGES[code]) return { general: ERROR_CODE_MESSAGES[code] };
  return { general: code ?? err.message };
}

export default function BootstrapOrgPage() {
  const { me, refresh } = useAuth();
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const trimmedName = name.trim();
    const trimmedCountry = country.trim().toUpperCase();

    const pre: FieldErrors = {};
    if (trimmedName.length < 2) {
      pre.name = 'Give your organization a name with at least 2 characters.';
    }
    if (trimmedCountry.length !== 0 && trimmedCountry.length !== 2) {
      pre.country = 'Use a 2-letter ISO country code (e.g. DK, US) or leave it blank.';
    }
    if (Object.keys(pre).length > 0) {
      setErrors(pre);
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/organizations/bootstrap', {
        name: trimmedName,
        country: trimmedCountry.length === 2 ? trimmedCountry : undefined,
      });
      await refresh();
    } catch (err) {
      setErrors(parseError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FB] p-6">
      <div className="w-full max-w-lg bg-white rounded-3xl shadow-xl border border-slate-100 p-10">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#6344C6] mb-2">
          Hi {me?.user.name.split(' ')[0]}
        </p>
        <h1 className="text-2xl font-bold mb-2">Create your organization</h1>
        <p className="text-sm text-slate-500 mb-8">
          You're the first person from your company here. Give it a name and you're in.
        </p>
        <form onSubmit={onSubmit} noValidate className="space-y-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Organization name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Robotics"
              aria-invalid={errors.name ? true : undefined}
              className={`mt-2 w-full border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 ${
                errors.name
                  ? 'border-rose-400 focus:ring-rose-400/40'
                  : 'border-slate-200 focus:ring-[#6344C6]/40'
              }`}
            />
            {errors.name && <p className="mt-1 text-sm text-rose-600">{errors.name}</p>}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">
              Country (ISO code, optional)
            </label>
            <input
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="DK"
              maxLength={2}
              aria-invalid={errors.country ? true : undefined}
              className={`mt-2 w-full border rounded-xl px-4 py-3 uppercase focus:outline-none focus:ring-2 ${
                errors.country
                  ? 'border-rose-400 focus:ring-rose-400/40'
                  : 'border-slate-200 focus:ring-[#6344C6]/40'
              }`}
            />
            {errors.country && <p className="mt-1 text-sm text-rose-600">{errors.country}</p>}
          </div>
          {errors.general && (
            <p className="text-sm text-rose-600" role="alert">
              {errors.general}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#6344C6] hover:bg-[#5a3eb8] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl py-3 transition-colors"
          >
            {submitting ? 'Creating…' : 'Create organization'}
          </button>
        </form>
      </div>
    </div>
  );
}
